import { useEffect, useState, useCallback } from "react";
import api, { API, formatErr } from "../../lib/api";
import { toast } from "sonner";
import MediaUploader from "../MediaUploader";
import { Eye, Save, Upload, X, RotateCcw, Mail, Bell, Package, Truck, Home, Ban, AlertCircle, ShoppingCart, Heart, PlayCircle } from "lucide-react";

const TEMPLATE_DEFS = [
  {
    id: "confirmation",
    label: "Order Confirmation",
    icon: Mail,
    desc: "Sent to the customer immediately after payment succeeds.",
    fields: [
      { key: "subject", label: "Subject", hint: "Use {brand}, {order_id}, {total} as placeholders" },
      { key: "eyebrow", label: "Eyebrow (small text above headline)" },
      { key: "headline", label: "Headline" },
      { key: "footer_body", label: "Footer message (shown below totals)", multiline: true },
    ],
    preview: { type: "confirmation" },
  },
  {
    id: "admin_alert",
    label: "Admin New-Order Alert",
    icon: Bell,
    desc: "Sent to your admin notification email when a new order comes in.",
    fields: [
      { key: "subject", label: "Subject", hint: "Use {brand}, {order_id}, {total} as placeholders" },
      { key: "headline", label: "Headline" },
      { key: "intro", label: "Intro text", multiline: true },
    ],
    preview: { type: "admin_alert" },
  },
  {
    id: "packed", label: "Order Packed", icon: Package, desc: "Sent when you mark the order as packed.",
    fields: [
      { key: "subject", label: "Subject" },
      { key: "headline", label: "Headline" },
      { key: "body", label: "Body", multiline: true },
    ],
    preview: { type: "lifecycle", status: "packed" },
  },
  {
    id: "shipped", label: "Order Shipped", icon: Truck, desc: "Sent when the courier picks up the order.",
    fields: [
      { key: "subject", label: "Subject" },
      { key: "headline", label: "Headline" },
      { key: "body", label: "Body", multiline: true },
    ],
    preview: { type: "lifecycle", status: "shipped" },
  },
  {
    id: "out_for_delivery", label: "Out for Delivery", icon: Truck, desc: "Sent the day the courier hits the customer's pin code.",
    fields: [
      { key: "subject", label: "Subject" },
      { key: "headline", label: "Headline" },
      { key: "body", label: "Body", multiline: true },
    ],
    preview: { type: "lifecycle", status: "out_for_delivery" },
  },
  {
    id: "delivered", label: "Delivered", icon: Home, desc: "Sent when the order is marked delivered.",
    fields: [
      { key: "subject", label: "Subject" },
      { key: "headline", label: "Headline" },
      { key: "body", label: "Body", multiline: true },
    ],
    preview: { type: "lifecycle", status: "delivered" },
  },
  {
    id: "refunded", label: "Refunded", icon: AlertCircle, desc: "Sent on refund processing.",
    fields: [
      { key: "subject", label: "Subject" },
      { key: "headline", label: "Headline" },
      { key: "body", label: "Body", multiline: true },
    ],
    preview: { type: "lifecycle", status: "refunded" },
  },
  {
    id: "cancelled", label: "Cancelled", icon: Ban, desc: "Sent when an order is cancelled.",
    fields: [
      { key: "subject", label: "Subject" },
      { key: "headline", label: "Headline" },
      { key: "body", label: "Body", multiline: true },
    ],
    preview: { type: "lifecycle", status: "cancelled" },
  },
  {
    id: "abandoned_cart",
    label: "Abandoned Cart",
    icon: ShoppingCart,
    desc: "Automated recovery email — sent 3 days after a customer adds a product to cart without buying. Includes product image, price, and CTA.",
    fields: [
      { key: "subject", label: "Subject", hint: "Use {brand}, {product_name} as placeholders" },
      { key: "headline", label: "Headline (below customer's name)" },
      { key: "body", label: "Body copy (shown above product image)", multiline: true },
      { key: "cta_label", label: "Button label" },
    ],
    preview: { type: "abandoned_cart" },
  },
  {
    id: "abandoned_wishlist",
    label: "Abandoned Wishlist",
    icon: Heart,
    desc: "Automated recovery email — sent 3 days after a customer adds a product to wishlist without buying.",
    fields: [
      { key: "subject", label: "Subject", hint: "Use {brand}, {product_name} as placeholders" },
      { key: "headline", label: "Headline (below customer's name)" },
      { key: "body", label: "Body copy (shown above product image)", multiline: true },
      { key: "cta_label", label: "Button label" },
    ],
    preview: { type: "abandoned_wishlist" },
  },
];

export default function EmailTemplatesAdmin() {
  const [settings, setSettings] = useState(null);
  const [activeId, setActiveId] = useState("confirmation");
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(null); // { url, label } | null

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateTpl = (id, key, value) => {
    setSettings(s => ({
      ...s,
      email_templates: {
        ...(s.email_templates || {}),
        [id]: { ...((s.email_templates || {})[id] || {}), [key]: value },
      },
    }));
  };

  const setLogo = ({ images }) => {
    setSettings(s => ({ ...s, email_logo_url: images[images.length - 1] || "" }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        email_logo_url: settings.email_logo_url || "",
        email_templates: settings.email_templates || {},
      });
      setSettings(data);
      toast.success("Email templates saved");
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  const resetField = (id, key) => {
    setSettings(s => {
      const next = { ...s, email_templates: { ...(s.email_templates || {}) } };
      const t = { ...((next.email_templates || {})[id] || {}) };
      delete t[key];
      next.email_templates[id] = t;
      return next;
    });
  };

  const openPreview = (def) => {
    const params = new URLSearchParams(def.preview);
    setPreviewing({ url: `${API}/admin/email-templates/preview?${params.toString()}`, label: def.label });
  };

  if (!settings) return <p className="text-muted text-sm py-12">Loading email templates…</p>;

  const active = TEMPLATE_DEFS.find(t => t.id === activeId);
  const ActiveIcon = active.icon;
  const activeValues = (settings.email_templates || {})[active.id] || {};

  return (
    <div className="space-y-10" data-testid="email-templates-admin">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4 pb-5 border-b border-[rgba(244,244,240,0.05)]">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-2">Atelier</p>
          <h2 className="font-serif text-4xl">Email Templates</h2>
          <p className="text-xs text-muted mt-2 max-w-xl">Customise every customer & admin email. Logo appears on every template. Leave any field blank to use the default copy.</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-gold" data-testid="save-templates-btn">
          <Save size={13} strokeWidth={1.5} className="inline mr-2 -mt-0.5" />
          {saving ? "Saving…" : "Save All Templates"}
        </button>
      </div>

      {/* Logo section */}
      <section className="bg-[#141414] p-6 border border-[rgba(244,244,240,0.05)]">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-1">Brand Logo</p>
            <p className="text-xs text-muted">Upload a PNG/SVG with transparent background, max 240×64px. Renders at the top of every email.</p>
          </div>
          {settings.email_logo_url && (
            <button
              onClick={() => setSettings(s => ({ ...s, email_logo_url: "" }))}
              className="text-[10px] uppercase tracking-[0.18em] text-muted hover:text-red-400"
              data-testid="remove-logo-btn"
            ><X size={11} className="inline -mt-0.5" /> Remove logo</button>
          )}
        </div>
        {settings.email_logo_url ? (
          <div className="bg-[#0C0C0C] p-6 flex items-center justify-center border border-[rgba(244,244,240,0.06)]">
            <img src={settings.email_logo_url} alt="Email logo preview" className="max-h-16 max-w-[240px]" data-testid="logo-preview" />
          </div>
        ) : (
          <MediaUploader images={[]} videos={[]} onChange={setLogo} accept="image" />
        )}
        {settings.email_logo_url && (
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted mt-3 break-all">{settings.email_logo_url}</p>
        )}
      </section>

      {/* Tabs + Editor */}
      <section className="grid lg:grid-cols-[260px_1fr] gap-6">
        {/* Tab list */}
        <nav className="bg-[#141414] p-2 border border-[rgba(244,244,240,0.05)] h-fit">
          <ul className="space-y-1">
            {TEMPLATE_DEFS.map(def => {
              const Icon = def.icon;
              const active = activeId === def.id;
              const hasOverride = !!(settings.email_templates || {})[def.id] && Object.values((settings.email_templates || {})[def.id] || {}).some(v => v);
              return (
                <li key={def.id}>
                  <button
                    onClick={() => setActiveId(def.id)}
                    className={`w-full text-left px-3 py-3 text-xs uppercase tracking-[0.18em] flex items-center gap-2 transition-colors ${active ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-muted hover:text-gold"}`}
                    data-testid={`template-tab-${def.id}`}
                  >
                    <Icon size={12} strokeWidth={1.5} />
                    <span className="flex-1 truncate">{def.label}</span>
                    {hasOverride && <span className="w-1.5 h-1.5 rounded-full bg-gold" title="Custom values set" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Editor */}
        <div className="bg-[#141414] p-6 border border-[rgba(244,244,240,0.05)] space-y-5">
          <div className="flex items-start justify-between flex-wrap gap-3 pb-5 border-b border-[rgba(244,244,240,0.05)]">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1 flex items-center gap-2"><ActiveIcon size={11} strokeWidth={1.5} /> Editing</p>
              <h3 className="font-serif text-2xl">{active.label}</h3>
              <p className="text-xs text-muted mt-1">{active.desc}</p>
            </div>
            <button onClick={() => openPreview(active)} className="btn-outline !px-4 !py-2 text-[10px]" data-testid={`preview-${active.id}-btn`}>
              <Eye size={11} strokeWidth={1.5} className="inline mr-1.5 -mt-0.5" /> Preview with sample data
            </button>
          </div>

          {active.fields.map(f => {
            const v = activeValues[f.key] || "";
            return (
              <div key={f.key}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-muted">{f.label}</label>
                  {v && (
                    <button onClick={() => resetField(active.id, f.key)} className="text-[9px] uppercase tracking-[0.18em] text-muted hover:text-gold" title="Reset to default">
                      <RotateCcw size={9} className="inline -mt-0.5" /> Reset
                    </button>
                  )}
                </div>
                {f.multiline ? (
                  <textarea
                    rows={4}
                    value={v}
                    onChange={e => updateTpl(active.id, f.key, e.target.value)}
                    placeholder="Leave blank to use default copy"
                    className="input-line w-full !min-h-[100px] !py-3 resize-y"
                    data-testid={`field-${active.id}-${f.key}`}
                  />
                ) : (
                  <input
                    type="text"
                    value={v}
                    onChange={e => updateTpl(active.id, f.key, e.target.value)}
                    placeholder="Leave blank to use default copy"
                    className="input-line w-full"
                    data-testid={`field-${active.id}-${f.key}`}
                  />
                )}
                {f.hint && <p className="text-[10px] text-muted/70 mt-1">{f.hint}</p>}
              </div>
            );
          })}

          {/* Placeholders cheat sheet */}
          <div className="bg-[#0C0C0C] p-4 border border-[rgba(244,244,240,0.05)]">
            <p className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">Available placeholders</p>
            <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.15em] text-muted">
              <code className="bg-[#141414] px-2 py-1">{"{brand}"}</code>
              {(active.id === "abandoned_cart" || active.id === "abandoned_wishlist") ? (
                <code className="bg-[#141414] px-2 py-1">{"{product_name}"}</code>
              ) : (
                <>
                  <code className="bg-[#141414] px-2 py-1">{"{order_id}"}</code>
                  <code className="bg-[#141414] px-2 py-1">{"{total}"}</code>
                </>
              )}
            </div>
          </div>

          {(active.id === "abandoned_cart" || active.id === "abandoned_wishlist") && (
            <AbandonedControls />
          )}
        </div>
      </section>

      {/* Preview modal */}
      {previewing && (
        <div className="fixed inset-0 z-[90] flex items-stretch" data-testid="preview-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setPreviewing(null)} />
          <div className="relative ml-auto bg-[#0C0C0C] border-l border-[rgba(244,244,240,0.08)] w-full max-w-3xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-[rgba(244,244,240,0.06)]">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Preview</p>
                <h3 className="font-serif text-xl">{previewing.label}</h3>
              </div>
              <button onClick={() => setPreviewing(null)} className="text-muted hover:text-gold" data-testid="close-preview-btn">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>
            <iframe
              src={previewing.url}
              title={`Preview: ${previewing.label}`}
              className="flex-1 w-full bg-[#0C0C0C]"
              data-testid="preview-iframe"
            />
            <div className="p-3 border-t border-[rgba(244,244,240,0.06)] text-[10px] uppercase tracking-[0.18em] text-muted text-center">
              Save your changes first to see them reflected in the preview · sample order data is shown
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function AbandonedControls() {
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [status, setStatus] = useState(null);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/abandoned/history");
      setHistory(data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Poll live status while a job is running
  useEffect(() => {
    if (!running) return undefined;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/abandoned/status");
        setStatus(data);
        if (!data.running) {
          clearInterval(iv);
          setRunning(false);
          setLastResult(data.last);
          if (data.last) {
            const sent = data.last.sent || 0;
            const failed = data.last.failed || 0;
            toast.success(`Recovery finished · ${sent} sent${failed ? ` · ${failed} failed` : ""}`, { duration: 5000 });
          }
          loadHistory();
        }
      } catch { /* silent — retry next tick */ }
    }, 2500);
    return () => clearInterval(iv);
  }, [running, loadHistory]);

  const runNow = async () => {
    if (!confirm("Kick off recovery emails now to every eligible customer? The job runs in the background.")) return;
    try {
      const { data } = await api.post("/admin/abandoned/run-now");
      if (data.already_running) {
        toast.message("A recovery job is already running — showing live status");
      } else {
        toast.success("Recovery job started in the background", { duration: 3000 });
      }
      setRunning(true);
      setStatus({ running: true, started_at: data.started_at, last: null });
    } catch (e) { toast.error(formatErr(e), { duration: 8000 }); }
  };

  return (
    <div className="bg-[#0C0C0C] p-4 border border-gold/20 space-y-3" data-testid="abandoned-controls">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-gold flex items-center gap-1.5"><PlayCircle size={11} strokeWidth={1.5} /> Recovery scheduler</p>
          <p className="text-[11px] text-muted mt-1 leading-relaxed">
            Runs automatically once per day at 09:00 UTC. Sends this email to customers whose add-to-cart / wishlist event happened 3–7 days ago and who never purchased. Each (customer, product) pair is emailed at most once. Manual run below spawns a background task so this admin page stays responsive.
          </p>
        </div>
        <button
          onClick={runNow}
          disabled={running}
          className="btn-outline !px-4 !py-2 text-[10px] whitespace-nowrap"
          data-testid="abandoned-run-now-btn"
        >
          {running ? "Running in background…" : "Run recovery now"}
        </button>
      </div>
      {running && status?.started_at && (
        <div className="text-[11px] text-muted bg-[#141414] p-3 border border-gold/30 flex items-center gap-3" data-testid="abandoned-running-indicator">
          <span className="inline-block w-2 h-2 rounded-full bg-gold animate-pulse" />
          Background job running since {new Date(status.started_at).toLocaleTimeString()} · this page will refresh when it finishes.
        </div>
      )}
      {lastResult && !running && (
        <div className="text-[11px] text-muted bg-[#141414] p-3 border border-[rgba(244,244,240,0.05)]" data-testid="abandoned-last-result">
          Last run · <span className="text-gold">{lastResult.sent}</span> sent · <span className="text-red-400">{lastResult.failed}</span> failed
          <span className="text-muted/70"> · cart {lastResult.by_kind?.abandoned_cart || 0} · wishlist {lastResult.by_kind?.abandoned_wishlist || 0}</span>
        </div>
      )}
      {history && (
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted" data-testid="abandoned-history-summary">
          Lifetime recoveries sent: <span className="text-gold">{history.total_sent}</span>
          {history.runs && history.runs.length > 0 && (
            <span className="ml-2 normal-case tracking-normal">· last ran {new Date(history.runs[0].ran_at).toLocaleString()}</span>
          )}
        </div>
      )}
    </div>
  );
}
